import React from 'react';
import { ShieldAlert, Building2, ArrowRight, LogOut, Globe } from 'lucide-react';
import { useWorkspaces } from './WorkspaceProvider';
import { getFrontendUrl, workspaceSubdomainHost } from '../../lib/platformDomain';

/**
 * Professional Access Denied experience shown when an authenticated user is
 * NOT a member of the workspace they requested (via URL subdomain / header).
 * The user is never silently redirected into another workspace.
 */
export default function WorkspaceAccessDenied({ onLogout }: { onLogout: () => void }) {
  const { accessDenied, memberships, switchWorkspace } = useWorkspaces();
  if (!accessDenied) return null;

  const rootUrl = getFrontendUrl();

  return (
    <div className="min-h-dvh premium-shell flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl border border-slate-200 w-full max-w-md overflow-hidden animate-fade-in">
        <div className="h-2 bg-gradient-to-r from-rose-500 to-amber-500" />
        <div className="p-8 text-center">
          <div className="w-16 h-16 rounded-2xl bg-rose-50 flex items-center justify-center mx-auto mb-5">
            <ShieldAlert className="h-8 w-8 text-rose-500" />
          </div>
          <h1 className="text-xl font-black text-slate-900">Access Denied</h1>
          <p className="text-sm text-slate-500 mt-2 leading-relaxed">
            You're signed in, but your account is <strong>not a member</strong> of the{' '}
            <span className="font-bold text-slate-700">{accessDenied.requestedSlug || 'requested'}</span> workspace.
          </p>

          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mt-5 text-left">
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 flex items-center gap-1.5">
              <Globe className="h-3 w-3" /> Requested Workspace
            </p>
            <p className="text-sm font-bold text-slate-800 font-mono">{accessDenied.requestedSlug ? workspaceSubdomainHost(accessDenied.requestedSlug) : '—'}</p>
            <p className="text-xs text-slate-500 mt-1">You do not have permission to access this workspace.</p>
          </div>

          <div className="mt-6 space-y-2.5">
            <a
              href={rootUrl}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-bold transition-all cursor-pointer"
            >
              Return to Platform Portal <ArrowRight className="h-4 w-4" />
            </a>

            {memberships.length > 0 && (
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-1.5">
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 px-1">Your Workspaces</p>
                {memberships.map(m => (
                  <button
                    key={m.workspaceId}
                    onClick={() => switchWorkspace(m.workspaceId)}
                    className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-white border border-transparent hover:border-slate-200 transition-all cursor-pointer text-left"
                  >
                    <div className="h-7 w-7 rounded-lg bg-brand/10 text-brand flex items-center justify-center shrink-0">
                      <Building2 className="h-3.5 w-3.5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-bold text-slate-800 truncate">{m.orgName || m.name}</p>
                      <p className="text-[10px] text-slate-400 capitalize">{m.role}</p>
                    </div>
                    <ArrowRight className="h-3.5 w-3.5 text-slate-300" />
                  </button>
                ))}
              </div>
            )}

            <button
              onClick={onLogout}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-slate-500 hover:text-rose-600 text-sm font-bold transition-all cursor-pointer"
            >
              <LogOut className="h-4 w-4" /> Sign Out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
